import { Button, Dialog, DialogTitle, DialogContent, IconButton, Typography, Box, Card, CardContent, Divider, Backdrop, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Close as CloseIcon, PlayArrow as PlayArrowIcon, School as SchoolIcon } from '@mui/icons-material';
import React, { useState } from 'react';
import { API } from '../../conf';

const TutorialButton = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [videosUrl, setVideosUrl] = useState([]);

  // Styled components
  const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
      borderRadius: theme.spacing(2),
      maxWidth: '600px',
      width: '90%',
    },
  }));

  const TutorialCard = styled(Card)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows[4],
    },
  }));

  const TutorialInfo = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  }));

  const TutorialDetails = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  }));

  const tutorialSections = [
    {
      title: "Versements",
      tutorials: [
        { 
          id: 1, 
          title: "Église avec compte bancaire", 
          art_name: "tuto/thyba versement bq tuto.mp4", 
          duration: "4 min",
          description: "Apprenez à gérer les versements pour une église avec compte bancaire."
        },
      ]
    },
    // {
    //   title: "Fonctionnalités de base",
    //   tutorials: [
    //     { 
    //       id: 3, 
    //       title: "Navigation dans l'interface", 
    //       art_name: "tuto/navigation-interface.mp4", 
    //       duration: "4 min",
    //       description: "Découvrez comment naviguer efficacement dans l'interface."
    //     },
    //     { 
    //       id: 4, 
    //       title: "Créer votre premier projet", 
    //       art_name: "tuto/premier-projet.mp4", 
    //       duration: "8 min",
    //       description: "Guide étape par étape pour créer et configurer votre premier projet."
    //     }
    //   ]
    // },
    // {
    //   title: "Fonctionnalités avancées",
    //   tutorials: [
    //     { 
    //       id: 5, 
    //       title: "Gestion des utilisateurs", 
    //       art_name: "tuto/gestion-utilisateurs.mp4", 
    //       duration: "6 min",
    //       description: "Apprenez à gérer les utilisateurs et leurs permissions."
    //     },
    //     { 
    //       id: 6, 
    //       title: "Intégrations externes", 
    //       art_name: "tuto/integrations-externes.mp4", 
    //       duration: "10 min",
    //       description: "Configurez et utilisez les intégrations avec des services externes."
    //     }
    //   ]
    // }
  ];

  const openDialog = () => setIsDialogOpen(true);
  const closeDialog = () => {
    setIsDialogOpen(false);
    setSelectedVideo(null);
  };

  const handleVideoClick = (tutorial) => {
    setLoading(true);
    // Verifier si l'URL de la vidéo est déjà en cache
    if(videosUrl.some(v => v.id === tutorial.id)) {
      setSelectedVideo({
        ...tutorial,
        videoUrl: videosUrl.find(v => v.id === tutorial.id).videoUrl
      });
      setLoading(false);
      return;
    } else {
      fetch(API+`/media/tuto?art_name=${encodeURIComponent(tutorial.art_name)}`)
      .then(async response => {
        if(!response.ok) {
          alert('Une erreur réseau est survenue lors de la récupération de la vidéo. Veuillez réessayer plus tard.');
          console.error('Erreur réseau lors de la récupération de l\'URL vidéo', response.status, response.statusText);
          throw new Error('Erreur réseau');
        }
        const video_url = await response.json();
        if (video_url) {
          setSelectedVideo({
            ...tutorial,
            videoUrl: video_url
          });
          // Mettre en cache l'URL de la vidéo
          setVideosUrl(prev => [...prev, { id: tutorial.id, videoUrl: video_url }]);
          setLoading(false);
        } else {
          alert('L\'URL de la vidéo est non disponible. Veuillez réessayer plus tard.');
          setLoading(false);
        }
      })
      .catch(error => {
        alert('Une erreur est survenue lors de la récupération de la vidéo. Veuillez réessayer plus tard.');
        console.error('Erreur lors de la récupération de l\'URL vidéo:', error);
        setLoading(false);
      });
    }
  };

  const handleBackToList = () => {
    setSelectedVideo(null);
  };

  return (
    <>
      <Button 
        variant="contained" 
        style={{backgroundColor: 'white'}}
        startIcon={<SchoolIcon />}
        onClick={openDialog}
        sx={{ 
          textTransform: 'none',
          borderRadius: 2,
          px: 3,
          py: 1,
          color: 'primary.main',
          fontWeight: 'bold',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: 'grey.100',
            boxShadow: 'none',
          }
        }}
      >
        Tutoriels
      </Button>

      <StyledDialog open={isDialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedVideo && (
              <IconButton onClick={handleBackToList} size="small">
                <PlayArrowIcon sx={{ transform: 'rotate(180deg)' }} />
              </IconButton>
            )}
            <Typography variant="h5" component="h2" fontWeight="bold">
              {selectedVideo ? selectedVideo.title : 'Tutoriels vidéo'}
            </Typography>
          </Box>
          <IconButton onClick={closeDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <Divider />
        
        <DialogContent sx={{ pt: 3, minHeight: '400px' }}>
          {selectedVideo ? (
            // Vue lecteur vidéo
            <Box>
              <Box sx={{ 
                width: '100%', 
                mb: 3,
                borderRadius: 2,
                overflow: 'hidden',
                boxShadow: 2
              }}>
                <video
                  width="100%"
                  height="315"
                  controls
                  autoPlay
                  style={{ display: 'block' }}
                >
                  <source src={selectedVideo.videoUrl} type="video/mp4" />
                  Votre navigateur ne supporte pas la lecture vidéo.
                </video>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {selectedVideo.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Durée: {selectedVideo.duration}
                </Typography>
                <Typography variant="body1">
                  {selectedVideo.description}
                </Typography>
              </Box>
            </Box>
          ) : (
            // Vue liste des tutoriels
            <>
              {tutorialSections.map((section, index) => (
                <Box key={index} sx={{ mb: 4 }}>
                  <Typography 
                    variant="h6" 
                    component="h3" 
                    sx={{ 
                      mb: 2, 
                      color: 'primary.main',
                      fontWeight: 'medium'
                    }}
                  >
                    {section.title}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {section.tutorials.map((tutorial) => (
                      <TutorialCard 
                        key={tutorial.id} 
                        onClick={() => handleVideoClick(tutorial)}
                        elevation={1}
                      >
                        <CardContent sx={{ py: 2 }}>
                          <TutorialInfo>
                            <PlayArrowIcon 
                              sx={{ 
                                color: 'primary.main', 
                                fontSize: 28 
                              }} 
                            />
                            <TutorialDetails>
                              <Typography 
                                variant="body1" 
                                fontWeight="medium"
                                sx={{ mb: 0.5 }}
                              >
                                {tutorial.title}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                              >
                                {tutorial.duration}
                              </Typography>
                            </TutorialDetails>
                          </TutorialInfo>
                        </CardContent>
                      </TutorialCard>
                    ))}
                  </Box>
                </Box>
              ))}
            </>
          )}
        </DialogContent>
      </StyledDialog>
      <Backdrop open={loading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}><CircularProgress color="inherit" /></Backdrop>
    </>
  );
};

export default TutorialButton;